/**
 * BottomTabNavigator — 5 tab mobile navigation: Chat, Agents, Workflow,
 * Research, Profile. Touch targets ≥48px. Active tab uses neural cyan.
 */
import { useEffect, useState } from "react";
import { MessageSquare, Bot, Workflow, Search, User } from "lucide-react";
import { useHaptics } from "./useHaptics";
import { cn } from "@/lib/utils";

export type MobileTab = "chat" | "agents" | "workflow" | "research" | "profile";

const TABS: { id: MobileTab; label: string; Icon: typeof MessageSquare }[] = [
  { id: "chat",     label: "Chat",     Icon: MessageSquare },
  { id: "agents",   label: "Agents",   Icon: Bot },
  { id: "workflow", label: "Workflow", Icon: Workflow },
  { id: "research", label: "Research", Icon: Search },
  { id: "profile",  label: "Profile",  Icon: User },
];

interface Props {
  active: MobileTab;
  onChange: (tab: MobileTab) => void;
}

export function BottomTabNavigator({ active, onChange }: Props) {
  const { trigger } = useHaptics();
  const [mounted, setMounted] = useState(false);
  useEffect(() => { setMounted(true); }, []);

  return (
    <nav
      role="tablist"
      aria-label="Primary"
      data-testid="bottom-tabs"
      className={cn(
        "fixed bottom-0 inset-x-0 z-40",
        "bg-background/95 backdrop-blur-xl border-t border-border/60",
        "pb-[env(safe-area-inset-bottom)]",
        mounted ? "translate-y-0" : "translate-y-full",
        "transition-transform duration-300",
      )}
    >
      <ul className="grid grid-cols-5">
        {TABS.map(({ id, label, Icon }) => {
          const isActive = active === id;
          return (
            <li key={id}>
              <button
                role="tab"
                aria-selected={isActive}
                data-testid={`tab-${id}`}
                onClick={() => { trigger("selection"); onChange(id); }}
                className={cn(
                  "w-full min-h-[56px] flex flex-col items-center justify-center gap-0.5 cursor-pointer",
                  "text-[10px] font-medium tracking-wide uppercase",
                  isActive ? "text-primary" : "text-muted-foreground",
                  "active:scale-[0.96] transition-transform",
                )}
              >
                <Icon size={22} strokeWidth={isActive ? 2.4 : 1.8} />
                <span>{label}</span>
                {isActive && <span className="absolute top-0 h-0.5 w-8 rounded-b bg-primary" />}
              </button>
            </li>
          );
        })}
      </ul>
    </nav>
  );
}
